'use client';

import React, { useState } from 'react';
import GenerateButton from '@/components/ui/GenerateButton';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';

interface NumberOptions {
    min: number;
    max: number;
    count: number;
    decimalPlaces: number;
    unique: boolean;
    sortAscending: boolean;
    allowNegative: boolean;
}

function GenerateNumbersPage() {
    const [options, setOptions] = useState<NumberOptions>({
        min: 1,
        max: 100,
        count: 5,
        decimalPlaces: 0,
        unique: false,
        sortAscending: false,
        allowNegative: false,
    });

    const [generatedNumbers, setGeneratedNumbers] = useState<string>('');

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? checked :
                ['min', 'max', 'count', 'decimalPlaces'].includes(name) ? Number(value) :
                    value,
        }));
    };

    const generateNumbers = () => {
        if (options.count < 1 || options.count > 1000) {
            return;
        }
        if (options.decimalPlaces < 0 || options.decimalPlaces > 10) {
            return;
        }
        let numbers: number[] = [];
        const min = options.allowNegative ? options.min : Math.max(0, options.min);
        const max = Math.max(min, options.max);

        for (let i = 0; i < options.count; i++) {
            let num = Math.random() * (max - min) + min;

            // Fix for decimal places
            num = Number(num.toFixed(options.decimalPlaces));

            // Ensure the number has exactly the specified decimal places
            const numStr = num.toFixed(options.decimalPlaces);
            num = Number(numStr);

            if (options.unique && numbers.includes(num)) {
                i--; // Try again
                continue;
            }

            numbers.push(num);
        }

        if (options.sortAscending) {
            numbers.sort((a, b) => a - b);
        }

        setGeneratedNumbers(numbers.map(n => n.toFixed(options.decimalPlaces)).join('\n'));
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Random Number Generator</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label">
                            <span className="label-text">Minimum Value</span>
                        </label>
                        <input
                            type="number"
                            className="input input-bordered w-full"
                            name="min"
                            value={options.min}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Maximum Value</span>
                        </label>
                        <input
                            type="number"
                            className="input input-bordered w-full"
                            name="max"
                            value={options.max}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Number of Numbers to Generate (1-1000)</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full ${options.count < 1 || options.count > 1000 ? 'input-error' : ''}`}
                            name="count"
                            min="1"
                            max="1000"
                            value={options.count}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Decimal Places (0-10)</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full ${options.decimalPlaces < 0 || options.decimalPlaces > 10 ? 'input-error' : ''}`}
                            name="decimalPlaces"
                            min="0"
                            max="10"
                            value={options.decimalPlaces}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm mr-2"
                                name="unique"
                                checked={options.unique}
                                onChange={handleOptionChange}
                            />
                            <span className="label-text">Generate Unique Numbers</span>
                        </label>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm mr-2"
                                name="sortAscending"
                                checked={options.sortAscending}
                                onChange={handleOptionChange}
                            />
                            <span className="label-text">Sort Numbers Ascending</span>
                        </label>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm mr-2"
                                name="allowNegative"
                                checked={options.allowNegative}
                                onChange={handleOptionChange}
                            />
                            <span className="label-text">Allow Negative Numbers</span>
                        </label>
                    </div>
                    <GenerateButton generatePasswords={generateNumbers} />
                </div>

                {/* Right Column - Generated Numbers */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated Numbers</h2>
                    <AutoGrowingTextarea
                        value={generatedNumbers}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateNumbersPage;